import { api } from "./client";
import type { InvoiceCustomerPayload } from "./invoices";
import { BUDGET_CLOSE_CARD_MS } from "./timeouts";
import type { KitchenHoldIn, KitchenHoldOut } from "./billKitchenHold";

export interface ModifierOut {
  id: number;
  modifier_id: string;
  created_at: string;
}

// Частта на една станция от ред на няколко станции (BE `station_statuses`,
// bills/station_routing.py); `tracked: false` — станция без кухненски монитор
// (само принтер): не движи общия статус на реда.
export interface StationStatusOut {
  station_id: number;
  status: string;
  tracked?: boolean;
}

export interface AdditionOut {
  id: number;
  addition_item_id: number;
  station_id: number;
  /** Частите на добавка на няколко станции (null — на една станция). */
  station_statuses?: StationStatusOut[] | null;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
  /** Процентът идва от „Промяна на цена", не от отстъпка → показва се като
   *  задраскана стара + нова цена, БЕЗ бадж за отстъпка. */
  custom_price: boolean;
  /** Преводите на добавката — пълнят се от `enrich_bill_with_status_names`. */
  item_names: Record<string, string>;
}

export interface BillElementItemOut {
  id: number;
  item_id: number;
  station_id: number;
  /** Частите на ред на няколко станции (null — ред на една станция);
   *  `tracked: false` — станция без кухненски монитор (само принтер). */
  station_statuses?: StationStatusOut[] | null;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
  status: string;
  status_name: string;
  /** Сервитьорската ос — отделна от кухненския статус ("0".."3"). */
  served: boolean;
  locked_price: boolean;
  /** Редът е продаден от обедно меню (org menus.id); null = обикновен ред. */
  lunch_menu_id?: number | null;
  /** Процентът идва от „Промяна на цена", не от отстъпка (виж AdditionOut). */
  custom_price: boolean;
  item_names: Record<string, string>;
  modifiers: ModifierOut[];
  additions: AdditionOut[];
}

export interface BillElementOut {
  id: number;
  waiter_id: number;
  order_time: string;
  comment: string | null;
  items: BillElementItemOut[];
  /** Само на партида, която някога е била задържана („Задържи") — виж
   *  `api/billKitchenHold.ts`; `order_time` остава часът на маркиране. */
  kitchen_hold?: KitchenHoldOut;
}

export interface DiscountAppliedOut {
  client_id: number;
  client_name: string;
  percent: number;
  discount_type: string;
}

// Едно теглене от картов терминал по сметката — ред от регистъра
// `card_payments` на BE (docs/card-payments.md, кросрепо ред 15).
// `amount` идва като НИЗ през response_model-ите (историята/справката) и като
// ЧИСЛО през dict отговорите (/bills) — винаги Number() преди аритметика.
export interface BillCardPayment {
  id: number;
  bill_id: number | null;
  bill_opened_at: string | null;
  printer_guid: string | null;
  terminal_txn_id: number | null;
  // "pc" (терминал на касата) | "fiscal" (терминал на принтера)
  mount: string;
  // consumed = пари + бон · unknown/void_failed = пари БЕЗ бон (тагът
  // „платена с карта — без бон") · voided = взети и веднага върнати
  status: string;
  amount: string | number;
  rrn: string | null;
  auth_id: string | null;
  masked_pan: string | null;
  receipt_number: string | null;
  request_id: string;
  refund_rrn: string | null;
  refund_note: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface BillOut {
  id: number;
  name: string;
  total: string;
  total_amount: string;
  total_discount: string;
  // Знаков процент на закачения клиент (<0 = надбавка). null = сметка без
  // клиент; 0 = клиент с 0% (валидно). „Има ли клиент" се гледа по
  // discount_applied, не по процента.
  discount_percent: number | null;
  waiter_id: number | null;
  customer_id: number | null;
  created_at: string;
  updated_at: string;
  status: string;
  closed_at: string | null;
  payment_method_id: number;
  // Кога сметката за последно е възстановена (отворена наново със същото id).
  restored_at?: string | null;
  // Теглене „пари взети, бон няма" по тази сметка (само closed + total 0) —
  // тагът „платена с карта — без бон". null/липсва при всичко останало.
  card_payment_pending?: BillCardPayment | null;
  elements: BillElementOut[];
  discount_applied: DiscountAppliedOut | null;
  // Настанената резервация на отворената сметка — името на гостите върху
  // картата на масата. Само в списъка `GET /bills` (BE
  // `service_notice.seated_reservations_of_bills`); липсва в единичните
  // отговори. В локален режим касата връща остаряло копие на облачното поле —
  // `TableList` не го показва там.
  reservation?: BillReservationTag | null;
  // Броят гости (`bills.guests`; null = не е въвеждан) — иконката върху
  // картата на масата. Само в списъка `GET /bills`, като `reservation`.
  guests?: number | null;
  // „Една сметка" на настанена резервация на няколко маси — масите ѝ, името
  // първо („14 | 13 | 12"); null — една маса. Само в списъка `GET /bills`
  // (BE `reservation_bills.joined_table_names` — сверено и със закритите
  // сметки на резервацията, които списъкът не вижда).
  table_names?: string[] | null;
  // Бакшишът, записан при закриването — само при бакшиш > 0 (BE ги пише
  // след `discount_applied`; сметка без бакшиш е байт за байт като преди).
  // Сумата идва като JSON ЧИСЛО, като `total_amount` в същия документ (dict
  // отговор) — винаги през `parseMoneyToCents`.
  tip_amount?: number | string;
  tip_payment_method_id?: number | null;
  tip_snapshot?: BillTipSnapshot | null;
  // Депозитът — само при депозит (BE ги пише последни): закаченият към
  // отворената сметка („Депозит" → „Използвай депозит") или платилият
  // закритата; `deposit_amount` е колко от поръчаното покрива — 0 докато
  // сметката е отворена (смята се при закриването). Името и цялата сума —
  // за панела на масата и „Закрий". Парите — JSON ЧИСЛО като `total_amount`.
  deposit_id?: number | null;
  deposit_amount?: number | string;
  deposit_name?: string | null;
  deposit_full_amount?: number | string | null;
  // Отстъпка в € на цялата сметка („Отстъпки" → „На цялата сметка") — само
  // при такава (BE ги пише последни). `bill_discount_amount` е въведената
  // сума, `bill_discount_applied` — действащата (не над сбора на редовете без
  // заключена цена). `total_amount` вече е с нея. JSON ЧИСЛО като `total_amount`.
  bill_discount_amount?: number | string;
  bill_discount_applied?: number | string;
}

/** `bills.tip_snapshot` — приложените настройки, записани ВЕДНЪЖ при
 *  закриването (никога не се преизчисляват по новите). Парите и процентът са
 *  низове. Един тип за документа на сметката и историята: методът в снимката
 *  е винаги число (BE `build_tip_snapshot`); null може да е само колоната
 *  `tip_payment_method_id` на реда (изтрит метод). */
export interface BillTipSnapshot {
  v: number;
  amount: string;
  payment_method_id: number;
  payment_method_name_bg: string | null;
  receipt_name_bg: string;
  receipt_name_en: string | null;
  receipt_note_bg: string | null;
  /** „A" | „B" | „C" — групата на реда в бона при закриването. */
  fiscal_tax_group: string;
  /** `voluntary_tip_no_vat` (група А) | `taxable` (група Б/В). */
  invoice_vat_treatment: string;
  expense_share_percent: string;
  expense_amount: string;
  staff_amount: string;
  deduct_card_tips: boolean;
  /** Ставката на реда във фактурата („0.00" | „20.00" | „9.00"). Липсва в
   *  снимките отпреди групите — те са група А, без ДДС. */
  invoice_vat_rate?: string;
}

/**
 * Бакшишът в тялото на `/close`, `/close/retry` и `/split-and-pay` — праща се
 * САМО при бакшиш > 0 (без бакшиш тялото е същото като преди). Сумите са
 * „x.xx". `total` в тялото е ДРУГО (0 = нефискален път) и не носи бакшиша.
 */
export interface CloseBillTipIn {
  tipAmount: string;
  tipPaymentMethodId: number;
  /** Поръчаното, което сервитьорът е прегледал, + бакшишът — BE го сверява
   *  със свежото `total_amount` под ключалката (`bill_total_changed`). */
  totalWithTip?: string;
  /** Само над прага за предупреждение — потвърдената СУМА. */
  tipConfirmedAmount?: string;
}

/** `reservation` по сметка в `GET /bills` — без телефон (сервитьорите нямат
 *  правото на модул „Резервации"). Името е за кутийката на масата
 *  (резервацията) и търсачката на списъка с маси, масите — за името на
 *  картата („14 | 13 | 12") и схемата. */
export interface BillReservationTag {
  id: number;
  guest_name: string;
  // ISO моменти — планираното начало и край на резервацията.
  start_at: string;
  end_at: string;
  guests: number;
  // По реда на резервацията (първата е масата на главната сметка).
  table_names: string[];
  // Забележката от резервацията (повод, алергии…); празната — null.
  note: string | null;
}

export interface FiscalResult {
  request_id: string;
  // "unknown" — FiscalAPI 2.6.3+: сторно, което може да е на хартия или
  // платено на апарата. BE го пази като "timeout" (G6).
  status: "success" | "failed" | "timeout" | "unknown";
  error?: string;
  result?: Record<string, unknown>;
}

// Изходът на документа е неизвестен — „може да е излязъл“, не отказ:
// кехлибареното съобщение, не червеното.
export const isUnknownFiscalOutcome = (status: FiscalResult["status"]) =>
  status === "timeout" || status === "unknown";

/**
 * „Фактуриране" → отворена сметка → купувач: закриването издава фактурата от
 * фискалното устройство (обща поредица на обекта). Тялото е BE
 * `InvoiceCustomerIn` + „Получил"; праща се САМО в `/close` — `/close/retry`
 * няма такова поле (намерението пътува в записания payload на бона).
 */
export interface CloseBillFiscalInvoiceIn {
  customer: InvoiceCustomerPayload;
  receivedBy?: string | null;
}

/** Издаденият при закриване/сторно документ — фискална фактура или кредитно
 *  известие: записът в „Фактури" и номерът на бона, който Е документът. */
export interface FiscalInvoiceRef {
  id: number;
  invoice_number: string;
  receipt_number: string | null;
}

/** Документът от ФУ Е на хартия, но записът му във „Фактури" не е станал (или е
 *  станал с уговорка) — готовият текст на BE: кой документ, защо и какво сега.
 *  Не е изгубен: fiscal_jobs редът е в „Фискални проблеми" (`invoice_issues`). */
export type FiscalDocumentRecordError = { bg: string; en: string };

/** Документът от ФУ в отговора: `fiscal_invoice`/`fiscal_invoice_error` от
 *  `/close` и `/close/retry`; `credit_note`/`credit_note_error` от „Сторнирай
 *  всичко", „Възстанови" и `/storno/retry`. */
export interface FiscalDocumentFields {
  /** Закриване (и повторното издаване) с фискална фактура — записаната
   *  фактура; null/липсва иначе. */
  fiscal_invoice?: FiscalInvoiceRef | null;
  fiscal_invoice_error?: FiscalDocumentRecordError | null;
  /** Сторно на сметка с фискална фактура — издаденото кредитно известие;
   *  null/липсва иначе. */
  credit_note?: FiscalInvoiceRef | null;
  credit_note_error?: FiscalDocumentRecordError | null;
}

export interface CloseBillResult extends BillOut, FiscalDocumentFields {
  fiscal_result?: FiscalResult;
}

export interface ModifierIn {
  modifier_id: string;
}

export interface AdditionIn {
  addition_item_id: number;
  station_id: number;
  quantity: string;
  price: string;
  percent: string;
  total_sum: string;
}

export interface ProductElementIn {
  productId: number;
  stationId: number;
  quantity: string;
  price: string;
  lockedPrice: boolean;
  percent: string;
  lineTotal: string;
  status?: string;
  modifiers?: ModifierIn[];
  addons?: AdditionIn[];
  /** Ред от обедно меню — `price` е обедната цена (виж lib/lunchCatalog.ts). */
  lunchMenuId?: number;
}

// Клиент, избран преди откриването (нова маса) — BE го закача веднага след
// създаването на сметката; същите полета като ApplyDiscountIn без billId.
export interface NewBillDiscountIn {
  clientId: number;
  // знаков процент: >0 = отстъпка, <0 = надбавка
  percent: number;
  clientName: string;
  discountType: string;
}

export interface CreateBillIn {
  billId: number;
  tableName?: string;
  elements: ProductElementIn[];
  comment?: string;
  // Само при billId=0 (нова маса); при добавяне към съществуваща се игнорира
  discount?: NewBillDiscountIn;
  // Само при billId=0: броят гости от модала преди „Нова маса" (настройка
  // „Въвеждане на брой гости" = при нова маса). BE: 1–999.
  guests?: number;
  // Само при billId=0: масата е на друг сервитьор (изборът на сервитьор в „Нова маса",
  // с `view_all_users_bills`). Праща се САМО при избран друг — касата в
  // локален режим го пренебрегва; поръчката остава на влезлия.
  waiterId?: number;
  // Времето за кухнята в „Преглед на поръчката" (`api/billKitchenHold.ts`) —
  // по група на време; праща се САМО при задържан ред, без него поръчката
  // влиза в кухнята веднага.
  kitchenHold?: KitchenHoldIn[];
}

export interface CancelBillItemIn {
  billElementId: number;
  billElementItemId: number;
  quantity?: number;
  reason?: string;
  isDefective?: boolean;
}

export interface SplitBillElementIn {
  billElementId: number;
  quantity: number;
}

export interface SplitBillIn {
  splitTableName: string;
  comment?: string | null;
  elements: SplitBillElementIn[];
  splitInExistingBill?: boolean;
  targetWaiterId?: number | null;
}

export interface UpdateItemStatusIn {
  itemId: number;
  billId: number;
  billElementId: number;
  // Уникален ид на конкретния ред (bill_element_items.id, а при добавка —
  // bill_element_item_additions.id). Когато е подаден, бекендът сменя статуса
  // само на този ред — нужно е, защото един продукт може да е на няколко
  // отделни реда в една поръчка (всяко добавяне при маркиране е отделен ред).
  billElementItemId?: number | null;
  isAddition?: boolean;
  status: "0" | "1" | "2" | "3";
  // Продукт на няколко станции (напр. Скара + Студена): станцията на
  // ЧАСТТА, чийто статус се сменя. Без нея (или за ред с една станция)
  // статусът важи за целия ред — така праща шпикерът. Мониторът я слага
  // само на части, никога на ред с една станция.
  stationId?: number;
}

export interface UpdateItemServedIn {
  billId: number;
  billElementId: number;
  /** bill_element_items.id — сервира се конкретният продуктов ред. */
  billElementItemId: number;
  served: boolean;
}

export interface UpdateItemStatusResponse {
  message: string;
  item_id: number;
  new_status: string;
}

export const getBills = async (): Promise<BillOut[]> => {
  const response = await api.get<BillOut[]>("/bills");
  return response.data;
};

// Проверката е ЕДНА заявка и нищо повече. Дотук при провал се правеше ВТОРА
// (`getBills()`) „ако endpoint-ът не съществува" — а той съществува и в
// облака, и на касата (`FiscalAPI app/edge/bills.py`:
// `GET /bills/check-table-name/{name}`). Тоест единственото, което резервът
// още вършеше, беше да УДВОИ чакането точно в аварията: 8 s за проверката
// плюс 8 s за списъка, преди сервитьорът да види каквото и да е. Провали ли
// се проверката, извикващият го казва и човекът опитва пак — повторението
// срещу касата е работа на интерсептора (`api/client.ts` §Повторение).
/**
 * Масата с това име се държи от резервация СЕГА (BE:
 * `reservations/service_notice.py`): настанена, чиято сметка е отворена
 * (напр. втората маса на резервация за 1+2), или предстояща, която започва в
 * следващия час / закъснява. Само за предупреждение — отварянето не спира.
 * Времената са в UTC с „Z".
 */
export interface TableReservationNotice {
  guestName: string;
  guests: number;
  status: "seated" | "upcoming";
  startAt: string;
  seatedAt: string | null;
  // Само при настанена: масата е ДРУГАТА маса на резервацията, сметката ѝ е
  // на тази („1") — отварянето е забранено (BE → 400), Маркиране показва отказ.
  billTableName?: string | null;
}

export interface TableNameCheck {
  exists: boolean;
  // null — няма резервация (или по-стар облак/каса без полето).
  reservation: TableReservationNotice | null;
  // Заета маса — сервитьорът на сметката, която я държи (без сумата); null —
  // свободна, „ничия" сметка от „Настани" или каса/по-стар облак без полето.
  takenBy: string | null;
}

// `billId` — „Смяна на маса" на тази сметка: при лична номерация номерът се
// сверява с масите на СОБСТВЕНИКА ѝ (масата на Мария → масите на Мария), не
// на питащия; без него (нова маса) — с масите на питащия.
export const checkTableName = async (
  tableName: string,
  billId?: number,
  // Нова маса на друг сервитьор — при лична номерация името се сверява с
  // неговите маси (BE иска `view_all_users_bills`).
  waiterId?: number,
): Promise<TableNameCheck> => {
  const params = { ...(billId ? { billId } : {}), ...(waiterId ? { waiterId } : {}) };
  const response = await api.get<{
    exists: boolean;
    reservation?: TableReservationNotice | null;
    takenBy?: string | null;
  }>(
    `/bills/check-table-name/${encodeURIComponent(tableName)}`,
    Object.keys(params).length > 0 ? { params } : undefined,
  );
  return {
    exists: response.data.exists,
    reservation: response.data.reservation ?? null,
    takenBy: response.data.takenBy ?? null,
  };
};

export interface NextTableName {
  name: string;
  // Резервацията, която държи предложената маса сега (като в
  // `check-table-name`); null — няма (или касата/по-стар облак без полето).
  reservation: TableReservationNotice | null;
}

// Предложен номер за „Нова маса" при бързо закриване. Смята се в BE-то,
// защото сервитьор без view_all_users_bills не вижда чуждите отворени маси,
// а при изключена „обща номерация" номерът трябва да е свободен глобално.
// Отговорът носи и `reservation` — затова питането „масата има резервация"
// не прави втора заявка `check-table-name`.
export const getNextTableName = async (): Promise<NextTableName> => {
  const response = await api.get<{ name: string; reservation?: TableReservationNotice | null }>(
    "/bills/next-table-name",
  );
  return { name: response.data.name, reservation: response.data.reservation ?? null };
};

export const createOrAddToBill = async (
  data: CreateBillIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>("/bills", data);
  return response.data;
};

/** Брой гости преди „Закрий" (настройка „Въвеждане на брой гости" = при
 * закриване). Само отворена сметка; право close_bill. Броят не се връща в
 * документа на сметката — касата строи същия документ байт за байт. */
export const setBillGuests = async (
  billId: number,
  guests: number,
): Promise<void> => {
  await api.patch(`/bills/${billId}/guests`, { guests });
};

export const closeBill = async (
  billId: number,
  paymentMethodId: number,
  mixedCashAmount?: number,
  mixedCardAmount?: number,
  total?: number,
  tip?: CloseBillTipIn,
  depositId?: number,
  fiscalInvoice?: CloseBillFiscalInvoiceIn,
): Promise<CloseBillResult> => {
  const payload: {
    paymentMethodId: number;
    mixedCashAmount?: number;
    mixedCardAmount?: number;
    total?: number;
    depositId?: number;
    fiscalInvoice?: CloseBillFiscalInvoiceIn;
  } & Partial<CloseBillTipIn> = {
    paymentMethodId,
  };

  if (
    paymentMethodId === 3 &&
    mixedCashAmount !== undefined &&
    mixedCardAmount !== undefined
  ) {
    payload.mixedCashAmount = mixedCashAmount;
    payload.mixedCardAmount = mixedCardAmount;
  }

  if (total !== undefined) {
    payload.total = total;
  }

  // Бакшишът — най-накрая и само ако го има (тяло без бакшиш = старото).
  if (tip) {
    Object.assign(payload, tip);
  }

  // Плащане с депозит — само с избран депозит (BE `deposits/service_bills.py`).
  if (depositId !== undefined) {
    payload.depositId = depositId;
  }

  // Фискална фактура — най-накрая и само с въведен купувач (без нея тялото е старото).
  if (fiscalInvoice) {
    payload.fiscalInvoice = fiscalInvoice;
  }

  const response = await api.post<CloseBillResult>(
    `/bills/${billId}/close`,
    payload,
  );
  return response.data;
};

export const cancelBillItem = async (
  billId: number,
  data: CancelBillItemIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/cancel-item`,
    data,
  );
  return response.data;
};

export interface SplitBillResponse {
  oldBill: BillOut;
  newBill: BillOut;
}

export const splitBill = async (
  billId: number,
  data: SplitBillIn,
): Promise<SplitBillResponse> => {
  const response = await api.post<SplitBillResponse>(
    `/bills/${billId}/split`,
    data,
  );
  return response.data;
};

// --- Split & pay ---
// Маркираме избрани продукти от сметката в нова сметка с автоматично име
// (напр. 101 → 101-001, 101-002 …) и веднага я затваряме с посочения
// метод на плащане. Името се формира от бекенда — фронтендът не го подава.
// Бакшишът (`Partial<CloseBillTipIn>`) — само при бакшиш > 0, без
// `totalWithTip` (виж `payment/SplitAndPayModal.tsx`).
export interface SplitAndPayBillIn extends Partial<CloseBillTipIn> {
  elements: SplitBillElementIn[];
  paymentMethodId: number;
  mixedCashAmount?: number;
  mixedCardAmount?: number;
  // При close_bill_1 → 5 клика на "в брой" изпраща total=0 (cash-only режим)
  total?: number;
}

export interface SplitAndPayBillResponse {
  oldBill: BillOut;
  newBill: BillOut;
  fiscal_result?: FiscalResult;
}

export const splitAndPayBill = async (
  billId: number,
  data: SplitAndPayBillIn,
): Promise<SplitAndPayBillResponse> => {
  const response = await api.post<SplitAndPayBillResponse>(
    `/bills/${billId}/split-and-pay`,
    data,
  );
  return response.data;
};

export const updateItemStatus = async (
  data: UpdateItemStatusIn,
): Promise<UpdateItemStatusResponse> => {
  const response = await api.patch<UpdateItemStatusResponse>(
    "/bills/items/status",
    data,
  );
  return response.data;
};

export const updateItemServed = async (
  data: UpdateItemServedIn,
): Promise<{ message: string; item_id: number; served: boolean }> => {
  const response = await api.patch<{
    message: string;
    item_id: number;
    served: boolean;
  }>("/bills/items/served", data);
  return response.data;
};

export interface ApplyDiscountIn {
  billId: number;
  clientId: number;
  percent: number;
  clientName: string;
  discountType: string;
}

export const applyDiscount = async (
  billId: number,
  data: ApplyDiscountIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/apply-discount`,
    data,
  );
  return response.data;
};

export const removeDiscount = async (billId: number): Promise<BillOut> => {
  const response = await api.post<BillOut>(`/bills/${billId}/remove-discount`);
  return response.data;
};

export interface ItemDiscountLineIn {
  billElementItemId: number;
  /** Колко бройки от реда получават процента; по-малко от реда → BE го разделя. */
  quantity: number;
  /** 0 = премахване на отстъпката. */
  percent: number;
}

export interface ApplyItemDiscountsIn {
  billId: number;
  lines: ItemDiscountLineIn[];
}

export interface SetItemPriceLineIn {
  billElementItemId: number;
  /** null = самият продукт; иначе id на добавката (`bill_element_item_additions.id`). */
  additionId: number | null;
  /** Новата единична цена на реда (не сумата). */
  price: number;
}

export interface SetItemPricesIn {
  billId: number;
  lines: SetItemPriceLineIn[];
}

export interface CancelAllItemsIn {
  reason?: string | null;
  isDefective?: boolean;
  // Сторно на сметка, платена с карта: облакът няма път до терминала —
  // операторът връща сумата от него и ПО ИЗБОР вписва референцията.
  // Сторното НЕ се блокира без нея (BE не я изисква).
  refundRrn?: string | null;
  refundNote?: string | null;
}

export interface RestoreBillIn extends CancelAllItemsIn {
  // Виж `transferBillWaiter`: възстановената сметка взима старото име и може да
  // дублира вече отворена маса. BE връща 409, докато не потвърдим изрично.
  allowDuplicateName?: boolean;
}

/**
 * 409 на „Възстанови", на което се пита „да има ли две маси с това име": само
 * тогава `detail` е ТЕКСТ (BE `restore_bill`). 409 с `{bg, en}` е отказ с
 * причина — кредитното известие на сметка с фискална фактура (вече издадено,
 * апаратът не може…); потвърждение не го променя, затова се показва, не пита.
 */
export const isRestoreDuplicateNameConflict = (error: unknown): boolean => {
  const response = (
    error as { response?: { status?: number; data?: { detail?: unknown } } } | null
  )?.response;
  return response?.status === 409 && typeof response.data?.detail === "string";
};

export const cancelAllBillItems = async (
  billId: number,
  data: CancelAllItemsIn,
): Promise<CloseBillResult> => {
  const response = await api.post<CloseBillResult>(
    `/bills/${billId}/cancel-all-items`,
    data,
  );
  return response.data;
};

export const restoreBill = async (
  billId: number,
  data: RestoreBillIn,
): Promise<CloseBillResult> => {
  const response = await api.post<CloseBillResult>(
    `/bills/${billId}/restore`,
    data,
  );
  return response.data;
};

export const renameBill = async (
  billId: number,
  name: string,
): Promise<BillOut> => {
  const response = await api.patch<BillOut>(`/bills/${billId}/name`, { name });
  return response.data;
};

// Прехвърля цялата сметка на друг сервитьор — променя само bills.waiter_id.
// bill_element.waiter_id (кой е приел конкретната поръчка) остава непроменен,
// за да се вижда в историята кой реално е приел всяка поръчка.
// `allowDuplicateName` се праща само след като операторът е потвърдил в модала:
// без него BE връща 409, ако получателят вече има отворена маса със същото име
// (законно при лична номерация — двама могат да имат маса „1").
export const transferBillWaiter = async (
  billId: number,
  waiterId: number,
  allowDuplicateName = false,
): Promise<BillOut> => {
  const response = await api.patch<BillOut>(`/bills/${billId}/waiter`, {
    waiterId,
    allowDuplicateName,
  });
  return response.data;
};

// Брой отворени маси по сервитьор — „N маси" до всяко име в модала за
// прехвърляне. Сервитьор без отворени маси не присъства в отговора.
export interface WaiterOpenBillsCount {
  waiterId: number;
  count: number;
}

export const getWaiterOpenCounts = async (): Promise<WaiterOpenBillsCount[]> => {
  const response = await api.get<WaiterOpenBillsCount[]>(
    "/bills/waiter-open-counts",
  );
  return response.data;
};

// Сервитьорите с неотчетен оборот (отворени + закрити, още не в персонален
// отчет), от най-големия към най-малкия — само id-тата, без сумите. За
// подредбата в избора на сервитьор на „Нова маса"; иска `view_all_users_bills`.
export const getWaitersByTurnover = async (): Promise<number[]> => {
  const response = await api.get<{ waiterIds: number[] }>(
    "/bills/waiters-by-turnover",
  );
  return response.data.waiterIds;
};

// Маса, заета от сметка на колега, която влезлият НЕ вижда (обща номерация,
// без `view_all_users_bills`) — само масата и сервитьорът, без сума и
// поръчано. Режимът „Схема" ги показва заети, когато няма „Настани".
export interface TakenTable {
  billId: number;
  tableName: string;
  // „Една сметка" за настанена резервация на няколко маси — всичките ѝ маси
  // (името първо); null — една маса.
  tableNames: string[] | null;
  openedAt: string;
  waiterId: number | null;
  waiterName: string | null;
}

export const getTakenTables = async (): Promise<TakenTable[]> => {
  const response = await api.get<TakenTable[]>("/bills/taken-tables");
  return response.data;
};

// „Прехвърли всичките ми маси": всички отворени сметки на ТЕКУЩИЯ потребител
// към друг сервитьор. Същият 409-договор като transferBillWaiter — detail
// изброява дублираните имена, FE пита и повтаря с allowDuplicateName.
export const transferAllBillsWaiter = async (
  waiterId: number,
  allowDuplicateName = false,
): Promise<{ transferred: number }> => {
  const response = await api.patch<{ transferred: number }>(
    "/bills/waiter/transfer-all",
    { waiterId, allowDuplicateName },
  );
  return response.data;
};

// Повторно закриване на закрита сметка без касова бележка (total = 0).
// Тялото е същото като на закриването — модалът е същият, включително
// cash-only режима (total: 0) и нефискалните методи. Без paymentMethodId се
// ползва методът по сметката: така „Фискални проблеми" повтаря СЪЩИЯ документ
// (защита срещу дублиран бон). Бакшишът НЕ се сменя при повторен опит —
// модалът не праща полетата му и BE ползва записания (`Partial<CloseBillTipIn>`
// е само за пълнота на договора).
export interface RetryCloseBillIn extends Partial<CloseBillTipIn> {
  paymentMethodId?: number;
  mixedCashAmount?: number;
  mixedCardAmount?: number;
  total?: number;
}

export const closeBillRetry = async (
  billId: number,
  data: RetryCloseBillIn,
  options?: { waitsForCardTerminal?: boolean },
): Promise<CloseBillResult> => {
  // Бюджетът по маршрут (`api/timeouts.ts`) се чете от ТЯЛОТО, а повторният
  // опит не носи бакшиша — записан бакшиш с карта към сметка в брой обаче
  // пак чака човек на терминала (BE `FISCAL_CARD_TIMEOUT`). Тогава
  // извикващият казва това изрично; без него заявката е буквално старата.
  const response = options?.waitsForCardTerminal
    ? await api.post<CloseBillResult>(`/bills/${billId}/close/retry`, data, {
        timeout: BUDGET_CLOSE_CARD_MS,
      })
    : await api.post<CloseBillResult>(`/bills/${billId}/close/retry`, data);
  return response.data;
};

/** Отстъпки в проценти върху избрани бройки от един или много продуктови реда
 *  наведнъж (добавките следват реда). Част от реда → BE го разделя на два.
 *  Процент 0 маха отстъпката. */
export const applyItemDiscounts = async (
  billId: number,
  data: ApplyItemDiscountsIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/apply-item-discounts`,
    data,
  );
  return response.data;
};

/** Отстъпка в € на цялата сметка; 0 я маха. Само облак (423 в локален режим). */
export const applyAmountDiscount = async (
  billId: number,
  amount: string,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/apply-amount-discount`,
    { billId, amount },
  );
  return response.data;
};

/** Нови единични цени на един или много реда наведнъж (BE ги пази като проценти). */
export const setItemPrices = async (
  billId: number,
  data: SetItemPricesIn,
): Promise<BillOut> => {
  const response = await api.post<BillOut>(
    `/bills/${billId}/set-item-prices`,
    data,
  );
  return response.data;
};

export interface PrintPreliminaryBillResult {
  status: "success" | string;
  result?: Record<string, unknown> | null;
}

/**
 * Предварителна сметка — печат на POS принтера на оператора за ОТВОРЕНА сметка.
 *
 * Нищо не се издава и нищо не се фискализира: BE-то праща моментна снимка
 * на сметката към FiscalAPI по свой топик (`print/preliminary_bill`), а
 * документът изрично пише „НЕ Е ФИСКАЛЕН БОН“. Затова е свободно повторим —
 * при грешка операторът просто натиска пак.
 */
export const printPreliminaryBill = async (
  billId: number,
): Promise<PrintPreliminaryBillResult> => {
  const response = await api.post<PrintPreliminaryBillResult>(
    `/bills/${billId}/preliminary-bill/print`,
  );
  return response.data;
};

export interface PrintReceiptCopyResult {
  status: "success" | string;
  result?: Record<string, unknown> | null;
}

/**
 * Копие на фискалния бон на ЗАТВОРЕНА сметка — печата се от електронния
 * журнал (КЛЕН) на апарата, издал бона, адресирано по `receipt_number`.
 *
 * Документът е нефискален (принтерът го маркира като копие) и не променя
 * нищо по сметката, затова е свободно повторим. BE-то отказва с 409 всичко
 * извън четирите условия (затворена, с номер, с ненулева сума, от днес) и с
 * 400, когато апаратът е от марка без такава команда.
 */
export const printReceiptCopy = async (
  billId: number,
): Promise<PrintReceiptCopyResult> => {
  const response = await api.post<PrintReceiptCopyResult>(
    `/bills/${billId}/receipt-copy/print`,
  );
  return response.data;
};
